import { NgIf, NgSwitch, NgSwitchCase } from '@angular/common';
import { AfterViewInit, Component, EventEmitter, Input, OnInit, Output, ViewChild, ViewEncapsulation } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonToggle, MatButtonToggleGroup } from '@angular/material/button-toggle';
import { MatDivider } from '@angular/material/divider';
import { MatIcon } from '@angular/material/icon';
import { MatTooltip } from '@angular/material/tooltip';
import { Observable, Subscription } from 'rxjs';
import { BullhornFieldType, Entities, ObjectType } from '../../data-rules-model';
import { IActionFieldMeta, MetaDataService, MetaSource } from '../../meta-data.service';
import { ICustomPositioningConfig } from '../custom-component-overlay.directive';
import { StaticOptionsProvider } from '../option-items-providers';
import { IEntityItem, PairedAutocompleteComponent } from '../paired-autocomplete/paired-autocomplete.component';

@Component({
  selector: 'app-toggle-group-btn',
  templateUrl: './toggle-group-btn.component.html',
  styleUrls: ['./toggle-group-btn.component.scss'],
  imports: [
    MatButtonToggleGroup,
    MatButtonToggle,
    MatTooltip,
    MatDivider,
    MatIcon,
    PairedAutocompleteComponent,
    NgSwitchCase,
    NgSwitch,
    NgIf,
    ReactiveFormsModule
  ],
  encapsulation: ViewEncapsulation.None
})

export class ToggleGroupBtnComponent implements OnInit, AfterViewInit {

  overlayPositioningConfig: ICustomPositioningConfig = {
    originX: 'center',
    originY: 'bottom',
    overlayX: 'center',
    overlayY: 'top',
    offsetY: 8,
    offsetX: -20
  };

  sourceValueControl: FormControl;
  sourceSecondaryDescriptor: FormControl;
  stringInput: FormControl;
  fieldValueControl: FormControl;
  selectedObjectTypeForm: FormControl;
  parametersFormGroup: FormGroup;
  sourceValueControl$: Subscription;
  sourceSecondaryDescriptor$: Subscription;
  @Input() primaryEntity: Entities;
  @Input() entitiesOptions: IEntityItem[];
  @Input() meta: Observable<IActionFieldMeta[]>;
  @Input() value: string;
  @Input() data: { value: string; sourceEntity: any; type: any; };
  @Input() metaType: string;
  formFieldType = ObjectType;
  destinationControl = null;
  selectedObjectType = null;
  dataSourceType = MetaSource.ACTION;
  pickerPlaceHolderText: string;
  fieldValue: string;
  inputValue: string;
  formInputType = BullhornFieldType;
  inputType: BullhornFieldType;

  @ViewChild('toggleGroup')
  sourceIdPickerComponent: PairedAutocompleteComponent;

  @Output() parameterForm: EventEmitter<{formGroup: FormGroup, sourceId: FormControl }> = new EventEmitter();
  @Output() secondaryEntity: EventEmitter<{secondEntity: string}> = new EventEmitter();

  constructor(private formBuilder: FormBuilder, private metaDataService: MetaDataService) {
  }

  ngAfterViewInit(): void {
  }

  ngOnInit(): void {
    if (this.metaType === 'string') {
      this.inputType = BullhornFieldType.STRING;
    }
    this.createLocalControls();
    this.loadControlData();
    if (this.data) {
      if (this.data.type === ObjectType.FIELD) {
        this.fieldValue = this.formatEntityValue(this.data);
        this.sourceSecondaryDescriptor.setValue(this.destinationControl);
      } else {
        this.inputValue = this.data.value;
      }
      this.changeEventFormTypeValue(this.data.type);
    } else {
      this.changeEventFormTypeValue(this.formFieldType.FIELD);
    }
  }

  changeEventFormTypeValue(value: ObjectType): void {
    this.parametersFormGroup.removeControl('sourceEntity');
    this.parametersFormGroup.removeControl('type');
    this.parametersFormGroup.removeControl('value');
    if (value === ObjectType.FIELD) {
      this.loadEntities();
      setTimeout(() => {
        this.loadPickerData();
      }, 0);
      this.selectedObjectType = this.formFieldType.FIELD;
      this.selectedObjectTypeForm.setValue(this.selectedObjectType);
      if (this.data) {
        this.sourceValueControl.setValue(this.fieldValue);
      }
      this.sourceAutocomplete(this.fieldValue);
    } else {
      this.selectedObjectType = this.formFieldType.VALUE;
      this.selectedObjectTypeForm.setValue(this.selectedObjectType);
      if (this.data) {
        this.stringInput.setValue(this.inputValue);
        this.valueChange(this.inputValue);
      }
    }
  }

  loadPickerData() {
    if (this.meta) {
      this.sourceIdPickerComponent.clearOption();
      this.meta.subscribe((filteredMeta: IActionFieldMeta[]) => {
        this.pickerPlaceHolderText = 'Loading...';
        const options = filteredMeta.filter(fm => (this.isAllowed(fm.metaType))).map(fm => ({name: fm.fieldName, label: fm.label}));
        const optionsProvider = new StaticOptionsProvider(options);
        this.sourceIdPickerComponent.setOptionsProvider(optionsProvider, this.sourceValueControl.value);
        this.pickerPlaceHolderText = undefined;
      });
    }
  }

  protected loadEntities(): void {
    this.metaDataService.reduceMetaToEntitiesOptions(this.primaryEntity, MetaSource.ACTION)
      .subscribe(entities => {
        this.entitiesOptions = entities;
      });
  }

  sourceAutocomplete(value: string) {
    if (this.destinationControl !== null && this.destinationControl !== undefined) {
      this.fieldValueControl.setValue(this.destinationControl + '.' + value);
    } else {
      this.fieldValueControl.setValue(value);
    }
    this.fieldValueControl.setValidators(Validators.required);
    this.fieldValue = value;
    this.parametersFormGroup.addControl('sourceEntity', this.sourceSecondaryDescriptor);
    this.parametersFormGroup.addControl('type', this.selectedObjectTypeForm);
    this.parametersFormGroup.addControl('value', this.fieldValueControl);
    this.parameterForm.emit({formGroup: this.parametersFormGroup, sourceId: this.sourceValueControl});
    if (this.fieldValueControl.value === null) {
      this.parametersFormGroup.setErrors({invalid: true});
    }
  }

  valueChange(value: string) {
    this.inputValue = value;
    if (this.data) {
      this.parametersFormGroup.addControl('type', this.selectedObjectTypeForm);
      this.parametersFormGroup.addControl('value', this.stringInput);
      this.parameterForm.emit({formGroup: this.parametersFormGroup, sourceId: this.sourceValueControl});
    }
    if (this.stringInput.value === null) {
      this.parametersFormGroup.setErrors({invalid: true});
    }
  }

  secondaryDescriptorChange(entityDescriptor: string) {
    if (entityDescriptor !== this.destinationControl) {
      this.destinationControl = entityDescriptor;
      this.fieldValueControl.setValue('');
      this.sourceValueControl.setValue('');
    }
    this.secondaryEntity.emit({secondEntity: entityDescriptor});
    setTimeout(() => {
      this.loadPickerData();
    }, 0);
  }

  createLocalControls() {
    this.sourceSecondaryDescriptor = new FormControl();
    this.fieldValueControl = new FormControl();
    this.sourceValueControl = new FormControl();
    this.stringInput = new FormControl();
    this.selectedObjectTypeForm = new FormControl();
    this.parametersFormGroup = this.formBuilder.group([]);
  }

  loadControlData() {
    if (this.sourceSecondaryDescriptor$) {
      this.sourceSecondaryDescriptor$.unsubscribe();
    }
    this.sourceSecondaryDescriptor$ = this.sourceSecondaryDescriptor.valueChanges.subscribe(sourceIdDesValue =>
      this.secondaryDescriptorChange(sourceIdDesValue));
    this.sourceSecondaryDescriptor.setValue(this.destinationControl);
    if (this.sourceValueControl$) {
      this.sourceValueControl$.unsubscribe();
    }
    this.sourceValueControl$ = this.sourceValueControl.valueChanges.subscribe(sourceIdValue =>
      this.sourceAutocomplete(sourceIdValue));
    if (this.data.sourceEntity) {
      this.destinationControl = this.data.sourceEntity;
    }
    this.fieldValueControl.setValue(this.data.value);
    this.selectedObjectTypeForm.setValue(this.data.type);
  }

  isAllowed(value: string) {
    if (this.inputType === BullhornFieldType.INTEGER) {
      switch (value) {
        case 'INTEGER':
          return true;
        case 'DOUBLE':
          return true;
        case 'BIG_DECIMAL':
          return true;
        default:
          return false;
      }
    } else if ((this.inputType === BullhornFieldType.STRING) && (value === 'STRING')) {
      return true;
    } else {
      return false;
      }
    }

  formatEntityValue(data) {
    let entityValue = data.value;
    if (data.sourceEntity) {
      if (data.value.indexOf('.') > 0) {
        entityValue = data.value.split('.')[1];
      }
    }
    return entityValue;
  }
}
